import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { Autocomplete, FormControl, TextField } from "@mui/material";

import { useSelector } from "react-redux";

import LoginButton from "../../components/molecules/LoginButton";
import SwalAlertComp from "../../components/SwalAlert";
import {
    getTiktokAdvertisers,
    getTiktokOfflineAsync,
    getTiktokPixelsAsync,
    tiktokReLogin,
} from "../../helpers/destinations/createDestination";
import SelectPixelComponent from "./SelectPixelComponent";
import "./tiktokComp.css";

const mapPixelsToChildren = (pixels, advertiserId) =>
    pixels?.map((el) => ({
        id: el.pixel_id,
        value: el.pixel_id,
        label: `${el.pixel_name} - ${el.pixel_id}`,
        parent_id: advertiserId,
    })) ?? [];

const TiktokCreateDest = ({
    authData,
    tiktokOfflineData,
    selectedType,
    selectedValues,
    setSelectedValues,
    pixelsOption,
    setPixelsOption,
}) => {
    const [selectedAdvertiserId, setSelectedAdvertiserId] = useState();
    const [isAuthData, setIsAuthData] = useState();
    const [offlineData, setOfflineData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isPixelsLoading, setIsPixelsLoading] = useState(false);
    const pixelRequestIdRef = useRef(0);
    const language = useSelector((state) => state.languagesState.languages);

    const advertisedIdsChanged = useMemo(() => {
        if (selectedType === "ttoffline") {
            return (
                offlineData?.map((item) => ({
                    id: item.advertiser_id,
                    value: item.advertiser_id,
                    label: `${item.advertiser_name} - ${item.advertiser_id}`,
                    name: item.advertiser_name,
                    children: item.offline_event_set_list?.map((el) => ({
                        id: el.event_set_id,
                        value: el.event_set_id,
                        label: `${el.event_set_name} - ${el.event_set_id}`,
                        parent_id: item.advertiser_id,
                    })),
                })) ?? []
            );
        }
        return (
            isAuthData?.map((item) => ({
                id: item.advertiser_id,
                value: item.advertiser_id,
                label: `${item.advertiser_name} - ${item.advertiser_id}`,
                name: item.advertiser_name,
            })) ?? []
        );
    }, [selectedType, offlineData, isAuthData]);

    useEffect(() => {
        if (authData) {
            setIsAuthData(authData);
        }
    }, [authData]);

    useEffect(() => {
        if (tiktokOfflineData) {
            setOfflineData(tiktokOfflineData);
        }
    }, [tiktokOfflineData]);

    const resetSelection = useCallback(() => {
        pixelRequestIdRef.current += 1;
        setIsPixelsLoading(false);
        setSelectedAdvertiserId(undefined);
        setSelectedValues([]);
        setPixelsOption(null);
    }, [setSelectedValues, setPixelsOption]);

    const fetchPixelsForAdvertiser = useCallback(
        async (advertiser) => {
            const requestId = ++pixelRequestIdRef.current;
            setIsPixelsLoading(true);
            setPixelsOption({ ...advertiser, children: [] });

            // advertiser.id is the account_id, it is a workaround for adblockers
            const res = await getTiktokPixelsAsync(advertiser.id);

            if (requestId !== pixelRequestIdRef.current) {
                return;
            }

            setIsPixelsLoading(false);

            if (res.status) {
                const payload = res.data ?? {};
                const pixels = Array.isArray(payload?.pixels)
                    ? payload.pixels
                    : [];
                setPixelsOption({
                    ...advertiser,
                    children: mapPixelsToChildren(pixels, advertiser.id),
                });
            } else {
                setPixelsOption({ ...advertiser, children: [] });
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: res?.message ?? "",
                    icon: "error",
                });
            }
        },
        [language, setPixelsOption],
    );

    const getAuthUrl = useCallback(async () => {
        const response = await tiktokReLogin();
        if (response?.status) {
            return response?.data?.auth_url ?? null;
        }
        SwalAlertComp({
            title: language?.delete_swal_modal?.failed ?? "Failed",
            message: response?.message,
            icon: "error",
        });
        return null;
    }, [language]);

    const getTiktokReLoginPixels = useCallback(
        async (data) => {
            setIsLoading(true);
            try {
                if (selectedType === "ttoffline") {
                    const res = await getTiktokOfflineAsync(data);
                    if (res.status) {
                        setOfflineData(res?.data ?? []);
                        resetSelection();
                    } else {
                        SwalAlertComp({
                            title:
                                language?.delete_swal_modal?.failed ?? "Failed",
                            message: res?.message ?? "",
                            icon: "error",
                        });
                    }
                } else {
                    const res = await getTiktokAdvertisers(data);
                    if (res.status) {
                        setIsAuthData(res?.data ?? []);
                        resetSelection();
                    } else {
                        SwalAlertComp({
                            title:
                                language?.delete_swal_modal?.failed ?? "Failed",
                            message: res?.message ?? "",
                            icon: "error",
                        });
                    }
                }
            } finally {
                setIsLoading(false);
            }
        },
        [language, selectedType, resetSelection],
    );

    const handleSelectAdvertiserId = useCallback(
        (advertiser) => {
            setSelectedValues([]);
            setSelectedAdvertiserId(advertiser);
            if (selectedType === "ttoffline") {
                setPixelsOption(advertiser);
                return;
            }
            if (advertiser) {
                fetchPixelsForAdvertiser(advertiser);
            }
        },
        [
            selectedType,
            fetchPixelsForAdvertiser,
            setSelectedValues,
            setPixelsOption,
        ],
    );

    const handleAdvertiserChange = useCallback(
        (_event, selected) => {
            handleSelectAdvertiserId(selected ?? undefined);
        },
        [handleSelectAdvertiserId],
    );

    const isPixelFlow = selectedType !== "ttoffline";

    return (
        <div className="relative">
            <div className="my-1">
                <div className="position-relative overflow-hidden z-1 mb-3">
                    <LoginButton
                        code="tiktok"
                        getAuthUrl={getAuthUrl}
                        paramNames={["auth_code", "state"]}
                        onSuccess={getTiktokReLoginPixels}
                        dataTransform={(params) => ({
                            code: params.auth_code,
                            state: params.state,
                        })}
                        logoSrc="/assets/images/typeImages/tiktok-icon.png"
                        buttonText={
                            language?.destination_modal?.reconnect_text ??
                            "Log in with different Tiktok Account"
                        }
                        isLoadingExternal={isLoading}
                    />
                </div>
                <FormControl fullWidth variant="outlined" className="my-2">
                    <Autocomplete
                        options={advertisedIdsChanged ?? []}
                        value={selectedAdvertiserId ?? null}
                        onChange={handleAdvertiserChange}
                        disabled={isLoading}
                        getOptionLabel={(option) => option?.label ?? ""}
                        isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    language?.destination_modal?.advertiser_id ??
                                    "Advertiser"
                                }
                            />
                        )}
                    />
                </FormControl>
            </div>
            <SelectPixelComponent
                data={pixelsOption}
                selectedType={selectedType}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                isLoading={isPixelFlow && isPixelsLoading}
                disabled={
                    isPixelFlow
                        ? !selectedAdvertiserId || isPixelsLoading
                        : !selectedAdvertiserId
                }
            />
        </div>
    );
};

export default TiktokCreateDest;
